import { useContext, useEffect, useLayoutEffect, useCallback, useState, useRef } from "react";

import ActionTypes from "./action-types";
import Context from "./reducer";
import getCookies from "./lib/getCookies";
import { formToObject } from "./lib/formToJSON";

export function useFetch(dispatch) {
  const [isSending, setIsSending] = useState(false);
  const doFetch = useCallback(
    ({ urlString, actionType, params, method = "GET", onSuccess = null }) => {
      const baseUrl = new URL(window.location.href);
      const url = new URL(urlString, baseUrl);
      const csrftoken = getCookies("csrftoken");
      const options = {
        method,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRFToken": csrftoken
        }
      };
      if (params) {
        if (method === "GET") {
          url.search = new URLSearchParams(params);
        } else {
          options.body = JSON.stringify(params);
        }
      }
      if (isSending) {
        return;
      }
      setIsSending(true);
      dispatch({ type: ActionTypes.LOADING });
      fetch(url, options)
        .then((response) => {
          if (!response.ok) {
            return Promise.reject(response);
          }
          if (response.ok && method !== "DELETE") {
            return response.json().then((data) => data);
          }
          if (response.ok && method === "DELETE") {
            dispatch({ type: actionType, urlString: urlString });
            setIsSending(false);
          }
        })
        .then(
          (data) => {
            dispatch({ type: actionType, data });
            setIsSending(false);
            if (onSuccess !== null) {
              const hasUrlString = onSuccess.urlString !== undefined;
              if (hasUrlString) {
                doFetch(onSuccess);
              }
              if (!hasUrlString) {
                dispatch({ type: onSuccess.actionType });
              }
            }
          },
          (errorResponse) => {
            errorResponse.json().then((errors) => {
              dispatch({ type: ActionTypes.ERROR, errors });
              setIsSending(false);
            });
          }
        )
        .catch((e) => console.error(e));
    },
    [dispatch, isSending]
  );
  return { doFetch };
}

/**
 * Run a function the first time a component is rendered
 * @param func the function to run
 * @param param the parameters to pass to the function
 */
export function useFirstUpdate(func, ...params) {
  const firstUpdate = useRef(true);
  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      func(...params);
      return;
    }
  });
}

export function useForm(urlString, actionType, onSuccess = null) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(Context);
  const { doFetch } = useFetch(dispatch);
  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (isSubmitting) return;
      setIsSubmitting(true);
      const params = formToObject(e.target);
      const method =
        actionType.startsWith("POST") || actionType.endsWith("POST")
          ? "POST"
          : actionType.startsWith("PATCH") || actionType.endsWith("PATCH")
          ? "PATCH"
          : actionType.startsWith("PUT") || actionType.endsWith("PUT")
          ? "PUT"
          : "GET";
      doFetch({ urlString, actionType, params, method, onSuccess });
      setIsSubmitting(false);
    },
    [actionType, doFetch, isSubmitting, onSuccess, urlString]
  );
  return { onSubmit, isSubmitting };
}

export function useAnimationFrame(callback) {
  const requestRef = useRef();
  const previousTimeRef = useRef();
  const animate = useCallback(
    (time) => {
      if (previousTimeRef.current !== undefined) {
        const deltaTime = time - previousTimeRef.current;
        callback(deltaTime);
      }
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    },
    [callback]
  );
  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [animate]); // Make sure the effect runs only once
}

export function useStateWithLocalStorage(localStorageKey, defaultValue) {
  const storedValue = JSON.parse(localStorage.getItem(localStorageKey));
  const [value, setValue] = useState(storedValue || defaultValue);
  useEffect(() => {
    localStorage.setItem(localStorageKey, JSON.stringify(value));
  }, [localStorageKey, value]);
  return [value, setValue];
}

export function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => {
        clearInterval(id);
      };
    }
  }, [callback, delay]);
}
